<script lang="ts" setup>
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import PkVoiceOrb from './PkVoiceOrb.vue'
    import PkChatButton from './PkChatButton.vue'

    /**
     * The live voice session, docked where the input pill normally is: while a
     * call is open this replaces it, so there is one place to look and one place
     * to hang up. Presentational on purpose, the session itself is owned by the
     * chat view, which is also what writes the transcript into the conversation.
     */
    const props = defineProps<{
        state: 'idle' | 'connecting' | 'listening' | 'speaking' | 'error'
        /** Loudness of whichever side is on air, sampled per animation frame. */
        sample: () => number
        spectrum?: () => Uint8Array | undefined
        remainingSeconds?: number
        muted?: boolean
        error?: string
        /**
         * The provider's own words for the failure, kept for the tooltip when
         * `error` is our localized copy for it.
         */
        rawError?: string
        /**
         * The socket is still open. The state collapses to 'error' whatever
         * the line is doing, and which actions make sense depends on the line:
         * a live failure is still billing and must offer the hang-up, a dead
         * one has nothing left to hang up.
         */
        live?: boolean
    }>()

    const emit = defineEmits<{
        'toggle-mute': []
        'hang-up': []
        retry: []
        dismiss: []
    }>()

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    /** One label for the whole session, because the user reads a state, not a protocol. */
    const stateLabel = computed(() => {
        if (props.error) {
            return $t('message.voiceFailed')
        }
        if (props.state === 'connecting') {
            return $t('message.voiceConnecting')
        }
        if (props.state === 'speaking') {
            return $t('message.voiceSpeaking')
        }
        if (props.muted) {
            return $t('message.voiceMuted')
        }
        return $t('message.voiceListening')
    })

    const remainingLabel = computed(() => {
        if (props.remainingSeconds === undefined) {
            return undefined
        }
        const minutes = Math.floor(props.remainingSeconds / 60)
        const seconds = props.remainingSeconds % 60
        return `${minutes}:${String(seconds).padStart(2, '0')}`
    })

    /** Warn only when the cap is close enough to interrupt a sentence. */
    const isRunningOut = computed(
        () =>
            props.remainingSeconds !== undefined &&
            props.remainingSeconds <= 30,
    )

    /** The line is open: audio is flowing in one direction or the other. */
    const isLive = computed(
        () =>
            !props.error &&
            (props.state === 'listening' || props.state === 'speaking'),
    )

    /**
     * Colours the state line: brand while the microphone is yours, accent
     * while the agent holds it, nothing while muted, connecting or failed.
     */
    const stateTone = computed(() => {
        if (!isLive.value) {
            return undefined
        }
        if (props.state === 'speaking') {
            return 'speaking'
        }
        return props.muted ? undefined : 'listening'
    })
</script>

<template>
    <div
        class="pk-voice-session"
        :class="{ 'pk-voice-session--failed': !!error }">
        <PkVoiceOrb
            :state="error ? 'error' : state"
            :sample="sample"
            :spectrum="spectrum" />

        <!-- Only the state is live: the timer ticks every second and would make
         a screen reader read the whole bar again with it -->
        <div class="pk-voice-session__status" role="status">
            <span
                class="pk-voice-session__state"
                :class="stateTone && `pk-voice-session__state--${stateTone}`">
                <!-- The dot marks the connection, the colour marks whose turn
                 it is: it stays on while muted (the line is still open) and
                 only the tone drops -->
                <span
                    v-if="isLive"
                    class="pk-voice-session__live-dot"
                    aria-hidden="true" />
                {{ stateLabel }}
            </span>
            <!-- A second line only when something broke: what was said is
             already in the conversation above, so repeating it here would only
             make the bar grow and shrink under the messages being read. The
             provider's own words survive in the tooltip when the line shows
             our copy for them -->
            <p
                v-if="error"
                class="pk-voice-session__detail"
                :title="rawError ?? error">
                {{ error }}
            </p>
        </div>

        <span
            v-if="remainingLabel"
            class="pk-voice-session__timer"
            :class="{
                'pk-voice-session__timer--warning': isRunningOut,
            }"
            :title="$t('hint.voiceRemaining')"
            aria-hidden="true">
            {{ remainingLabel }}
        </span>

        <!-- Same icon buttons as the input pill this bar replaces: a call must
         not look like a different piece of software from the thing it took
         over. Recovering from a failure keeps its words, an icon-only refresh
         is not a recovery path -->
        <div class="pk-voice-session__actions">
            <template v-if="error">
                <PkChatButton
                    icon="ri:refresh-line"
                    :label="$t('action.retry')"
                    @click="emit('retry')" />
                <!-- Two actions, chosen by the line rather than three at once.
                 A failure does not always end the call: on a socket still open
                 (and still billing) the way out is hanging up, while dismissing
                 would hide a line that keeps costing. On a dead socket there is
                 nothing left to hang up, and closing gives the input back -->
                <PkChatButton
                    v-if="live"
                    icon="ri:stop-circle-line"
                    modifiers="danger"
                    :title="$t('action.hangUp')"
                    :aria-label="$t('action.hangUp')"
                    @click="emit('hang-up')" />
                <PkChatButton
                    v-else
                    icon="ri:close-line"
                    :label="$t('action.close')"
                    @click="emit('dismiss')" />
            </template>
            <template v-else>
                <PkChatButton
                    :icon="muted ? 'ri:mic-off-line' : 'ri:mic-line'"
                    :title="muted ? $t('action.unmute') : $t('action.mute')"
                    :aria-label="
                        muted ? $t('action.unmute') : $t('action.mute')
                    "
                    :aria-pressed="muted"
                    @click="emit('toggle-mute')" />
                <PkChatButton
                    icon="ri:stop-circle-line"
                    modifiers="danger"
                    :title="$t('action.hangUp')"
                    :aria-label="$t('action.hangUp')"
                    @click="emit('hang-up')" />
            </template>
        </div>
    </div>
</template>

<style lang="scss">
    // Sits where the input sits and is built out of its box: same margins, same
    // rounding, same surface, same resting height, so answering by voice does
    // not move the dock. The error state stays inside it rather than pushing a
    // second block below, which is what made a failure look like a stack trace.
    .pk-voice-session {
        // Above the ambient glow, which is painted by an absolutely positioned
        // sibling: the input pill it replaces raises itself the same way
        position: relative;
        z-index: 1;
        // The dock is 50px tall in the widget and 60px in fullscreen. It is
        // declared, not derived: deriving it from the tallest child means the
        // box moves whenever a child changes, and this box has to be exactly
        // as tall as the input it took the place of.
        --pk-chat-dock-height: var(--spacing-50);
        // Free to be as big as it looks right, now that nothing hangs off it:
        // the height above is a floor no child reaches. It stays well inside
        // that floor, because a filled disc reads heavier than its diameter
        // and the state next to it is what should be read first.
        --pk-voice-orb-size: var(--spacing-32);
        --pk-voice-orb-bars-height: var(--spacing-18);
        // Named, because the failure tint is mixed into it and fullscreen
        // swaps it: a plain `background-color` there would outrank the
        // modifier and paint a failed call as a healthy one.
        --pk-voice-session-surface: var(--color-surface);
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--spacing-12);
        // A floor rather than a height: a failure adds a second line, and the
        // reason of it is worth more than a pixel-perfect box.
        min-height: var(--pk-chat-dock-height);
        margin-inline: var(--spacing-sm);
        margin-bottom: var(--spacing-sm);
        padding-block: 0;
        padding-inline: var(--spacing-14);
        border: 1px solid var(--color-surface-3);
        border-radius: var(--rounded-xl);
        background-color: var(--pk-voice-session-surface);
        box-shadow: var(--shadow-lg);

        @include media-breakpoint-up('sm', $breakpoints) {
            padding-inline: var(--spacing-10);
        }

        &--failed {
            background-color: color-mix(
                in srgb,
                var(--color-danger) 6%,
                var(--pk-voice-session-surface)
            );
        }

        // Takes the room that is left and gives all of it back when the
        // actions need it: with a basis of its own it pushed them onto a
        // second row in the widget, which is the one place with no room to
        // spare. The label itself is a single ellipsed line, so it has nothing
        // to lose by shrinking.
        &__status {
            display: flex;
            min-width: 0;
            flex: 1 1 auto;
            flex-direction: column;
        }

        &__state {
            overflow: hidden;
            font-size: var(--text-14);
            color: var(--color-word-3);
            text-overflow: ellipsis;
            white-space: nowrap;

            // The turn colours match the orb: brand is "you are being heard",
            // accent is "the agent has the floor". Muted and connecting stay
            // in the resting grey, because grey is what makes these read.
            &--listening {
                color: var(--color-brand);
                font-weight: var(--font-medium, 500);
            }

            &--speaking {
                color: var(--color-accent);
                font-weight: var(--font-medium, 500);
            }
        }

        // Blinks as long as the socket is up, whatever the colour of the text
        // around it: a live line and an open microphone are two different
        // facts, and muting must not look like hanging up.
        &__live-dot {
            display: inline-block;
            width: var(--spacing-6);
            height: var(--spacing-6);
            margin-inline-end: var(--spacing-4);
            border-radius: var(--rounded-full);
            background-color: currentcolor;
            vertical-align: middle;
            animation: pk-voice-session-live 2s ease-in-out infinite;
        }

        // Two lines at most: a provider failure can be a long sentence, and the
        // whole of it is on the title attribute.
        &__detail {
            display: -webkit-box;
            overflow: hidden;
            margin: 0;
            color: var(--color-word-3);
            font-size: var(--text-12);
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            line-clamp: 2;
        }

        &--failed &__detail {
            color: var(--color-danger-darken-1);
        }

        &__timer {
            color: var(--color-word-3);
            font-size: var(--text-12);
            font-variant-numeric: tabular-nums;

            &--warning {
                color: var(--color-danger);
            }
        }

        &__actions {
            display: flex;
            align-items: center;
            gap: var(--spacing-xs);
            margin-inline-start: auto;
        }
    }

    @keyframes pk-voice-session-live {
        0%,
        100% {
            opacity: 1;
        }
        50% {
            opacity: 0.35;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .pk-voice-session__live-dot {
            animation: none;
        }
    }
</style>
